import { makeAutoObservable } from 'mobx'
import {
  AdditionalBreakpointsEnum,
  BreakpointsEnum,
  type IBreakpoint,
} from 'shared/constants/breakpoints'
import { IS_APP_LIGHT } from 'shared/config'

const MIN_SUPPORT_BREAKPOINT = IS_APP_LIGHT ? BreakpointsEnum.MobileXS : BreakpointsEnum.XS

if (IS_APP_LIGHT) {
  document.body.dataset.isSupportMobileView = 'true'
}

export class LayoutStore {
  activeBreakpoint: IBreakpoint | null = null
  globalActiveBreakpoint: IBreakpoint | null = null
  width: number | null = null
  private _breakpoints = BreakpointsEnum

  constructor() {
    makeAutoObservable(this)
    this.handleActiveBreakpoint()
    window.addEventListener('resize', this.handleActiveBreakpoint)
  }

  private handleActiveBreakpoint = () => {
    const width = document.documentElement.clientWidth
    this.width = width

    const breakpoints = Object.values(this.breakpoints)

    const breakpoint =
      breakpoints.find((breakpoint, index) => {
        if (index === 0 && width <= breakpoint) {
          return true
        }
        if (width >= breakpoint && width < breakpoints[index + 1]) {
          return true
        }
        if (index === breakpoints.length - 1 && width >= breakpoint) {
          return true
        }
      }) || breakpoints[breakpoints.length - 1]

    if (breakpoint >= MIN_SUPPORT_BREAKPOINT) {
      this.activeBreakpoint = breakpoint
    } else {
      this.activeBreakpoint = MIN_SUPPORT_BREAKPOINT
    }
    this.globalActiveBreakpoint = breakpoint
  }

  get isXsMobileView() {
    return this.activeBreakpoint && this.activeBreakpoint === this.breakpoints.MobileXS
  }

  get isMobileView() {
    return this.activeBreakpoint && this.activeBreakpoint < this.breakpoints.XXS
  }

  get isViewXXsGlobal() {
    return this.globalActiveBreakpoint && this.globalActiveBreakpoint <= this.breakpoints.XXS
  }

  get isViewXs() {
    return this.activeBreakpoint === this.breakpoints.XS
  }

  get isMediumView() {
    return this.activeBreakpoint && this.activeBreakpoint <= this.breakpoints.M
  }

  get isLargeView() {
    return this.activeBreakpoint && this.activeBreakpoint >= this.breakpoints.L
  }

  get isXlView() {
    return this.activeBreakpoint && this.activeBreakpoint >= this.breakpoints.XL
  }

  get isMobileViewWithoutMSize() {
    return this.activeBreakpoint && this.activeBreakpoint <= this.breakpoints.S
  }

  get isAdditionalXSView() {
    return !!this.width && this.width < AdditionalBreakpointsEnum[1080]
  }

  get breakpoints() {
    return this._breakpoints
  }

  setBreakpoints = (breakpoints: typeof BreakpointsEnum) => {
    this._breakpoints = breakpoints
    this.handleActiveBreakpoint()
  }

  setDefault = () => {
    this._breakpoints = BreakpointsEnum
    this.handleActiveBreakpoint()
  }
}

export const layoutStore = new LayoutStore()
